// ABOUTME: Plans the merge of a guest dashboard into an authenticated one.
// ABOUTME: Re-indexes guest items so they append rather than collide on (col, order).

import type { DashboardItem } from "../services/dashboardService";
import { normalizeUrlKey } from "./urlValidation";
import { MAX_DASHBOARD_ITEMS } from "./bookmarkImport";

export interface MergeResult {
  items: DashboardItem[];
  /** Guest items dropped because the user already had them. */
  duplicates: number;
  /** Guest items dropped because the merged doc would exceed the rules cap. */
  overLimit: number;
}

/** Identity for dedupe: links by URL, widgets by name (two Scratchpads is nonsense). */
function identityOf(item: DashboardItem): string | null {
  if (item.type === "widget") {
    return item.widgetName ? `widget:${item.widgetName}` : null;
  }
  return item.url ? `link:${normalizeUrlKey(item.url)}` : null;
}

/**
 * Merges guest items into an existing dashboard.
 *
 * The previous implementation was `[...existing, ...guest]` with no
 * re-indexing, so a guest item at (0, 0) collided with an existing item at
 * (0, 0). The renderer sorts by `order` with no tiebreak, so the merged
 * dashboard came out in arbitrary order and looked shuffled.
 */
export function mergeGuestItems(
  existingItems: DashboardItem[],
  guestItems: DashboardItem[],
  options: { maxItems?: number } = {}
): MergeResult {
  const maxItems = options.maxItems ?? MAX_DASHBOARD_ITEMS;

  const seen = new Set(existingItems.map(identityOf).filter((k): k is string => k !== null));

  // Next free slot per column, so guest items land after what's already there.
  const nextOrder = new Map<number, number>();
  for (const item of existingItems) {
    const col = typeof item.col === "number" ? item.col : 0;
    const order = typeof item.order === "number" ? item.order : 0;
    nextOrder.set(col, Math.max(nextOrder.get(col) ?? 0, order + 1));
  }

  let remaining = Math.max(0, maxItems - existingItems.length);
  const merged: DashboardItem[] = [...existingItems];
  let duplicates = 0;
  let overLimit = 0;

  // Preserve the guest's own arrangement as the relative order we append in.
  const ordered = [...guestItems].sort((a, b) => {
    const colA = typeof a.col === "number" ? a.col : 0;
    const colB = typeof b.col === "number" ? b.col : 0;
    if (colA !== colB) return colA - colB;
    return (a.order ?? 0) - (b.order ?? 0);
  });

  for (const item of ordered) {
    const key = identityOf(item);
    if (key && seen.has(key)) {
      duplicates += 1;
      continue;
    }
    if (remaining <= 0) {
      overLimit += 1;
      continue;
    }
    if (key) seen.add(key);
    remaining -= 1;

    const col = typeof item.col === "number" ? item.col : 0;
    const order = nextOrder.get(col) ?? 0;
    nextOrder.set(col, order + 1);

    merged.push({ ...item, col, order });
  }

  return { items: merged, duplicates, overLimit };
}
